// Adding a banner picture (ADR 0101): check the file here so the owner hears at once, then
// the same three steps as a dish photo (mint a presigned PUT, send the bytes, confirm).
// The picture is cropped to 16:9 from the middle by the server; the limits mirror its own.

import { apiFetch } from './client.js';
import { putWithProgress } from './photo-upload.js';
import type { BannerView, MediaUploadUrlResponse } from './types.js';

export const BANNER_LIMITS = { maxPerMenu: 8, maxBytes: 10 * 1024 * 1024 } as const;

const TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
const BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
};

export type BannerFileCheck =
  | { readonly ok: true; readonly contentType: string }
  | { readonly ok: false; readonly message: string };

export function checkBannerFile(file: File): BannerFileCheck {
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  const contentType = file.type !== '' ? file.type : (BY_EXTENSION[ext] ?? '');
  if (file.size === 0) return { ok: false, message: 'This file is empty.' };
  if (!TYPES.includes(contentType)) {
    return { ok: false, message: 'Use a JPEG, PNG, WebP or AVIF picture.' };
  }
  if (file.size > BANNER_LIMITS.maxBytes) {
    return { ok: false, message: 'Pictures can be up to 10 MB.' };
  }
  return { ok: true, contentType };
}

export async function uploadBanner(args: {
  readonly basePath: string; // "/organizations/:orgId/menus/:menuId/banners"
  readonly file: File;
  readonly onProgress?: (fraction: number) => void;
}): Promise<BannerView> {
  const check = checkBannerFile(args.file);
  if (!check.ok) throw new Error(check.message);
  const minted = await apiFetch<MediaUploadUrlResponse>(`${args.basePath}/upload-url`, {
    method: 'POST',
    body: { contentType: check.contentType, sizeBytes: args.file.size },
  });
  await putWithProgress(
    minted.uploadUrl,
    args.file,
    minted.requiredHeaders,
    args.onProgress,
    undefined,
  );
  const confirmed = await apiFetch<{ banner: BannerView }>(`${args.basePath}/confirm`, {
    method: 'POST',
    body: { objectKey: minted.objectKey },
  });
  return confirmed.banner;
}
